"use client";

import { useEffect } from "react";

import { readStoredConsent } from "@/lib/consent-state";
import { scrubUrlProperties } from "@/lib/analytics-url";
import { getBaseAnalyticsProps } from "@/lib/analytics-base-props";
import {
  flushPosthogQueue,
  setPosthogClient,
  setPosthogConsent,
} from "@/lib/posthog-events";

let initialized = false;

// Retry a transient SDK-chunk load failure with backoff so a momentary network
// blip on first load doesn't disable analytics/replay for the whole session.
const MAX_LOAD_ATTEMPTS = 3;

interface PostHogAnalyticsProps {
  readonly apiKey?: string;
  readonly apiHost?: string;
  readonly uiHost?: string;
  readonly environment?: string;
  readonly captureBotTraffic?: boolean;
}

/**
 * Loads and initializes posthog-js, consent-gated against sq_consent. Mirrors
 * components/plausible-analytics.tsx: dark unless apiKey is set (so local dev and
 * tests stay clean), lazy dynamic import so the SDK is never in the initial bundle.
 *
 * Consent model: the SDK always boots opted out on in-memory persistence — no
 * cookies, no events. The stored consent decision is read and applied in the
 * loaded callback (after the async import + remote config settle), so a consent
 * change made while the SDK is still loading is reflected by that final read
 * rather than lost. Runtime changes after load come through lib/consent.ts ->
 * setPosthogConsent directly.
 *
 * Session replay never auto-starts (`disable_session_recording`); it runs only
 * where PostHogReplayGate allows it — anonymous visitors on marketing pages —
 * and is fully masked when it does. Event capture is independent and always on
 * (subject to consent): the hire funnel, source attribution and cost reporting
 * are built on it.
 */
export function PostHogAnalytics({
  apiKey,
  apiHost,
  uiHost,
  environment,
  captureBotTraffic,
}: PostHogAnalyticsProps) {
  useEffect(() => {
    if (!apiKey || initialized) return;
    initialized = true;

    const host = apiHost ?? "https://eu.i.posthog.com";
    const ui = uiHost ?? "https://eu.posthog.com";

    const load = (attempt: number): void => {
      import("posthog-js")
        .then(({ default: posthog }) => {
          posthog.init(apiKey, {
            api_host: host,
            ui_host: ui,
            // Always boot dark; the loaded callback applies the real consent state.
            opt_out_capturing_by_default: true,
            persistence: "memory",
            // Auto-capture pageviews including SPA history changes (App Router).
            capture_pageview: "history_change",
            capture_pageleave: true,
            // posthog-js silently drops captures from bot/headless user agents.
            // Off by default; only enabled where explicitly configured (local /
            // automated browser tests), never inferred from NODE_ENV.
            opt_out_useragent_filter: captureBotTraffic ?? false,
            // REPLAY NEVER AUTO-STARTS. It runs only while
            // components/posthog-replay-gate.tsx says the current page is
            // anonymous AND on the marketing allowlist; everything else — the
            // signed-in app, public deployed apps, unknown routes — leaves the
            // recorder stopped. Default-deny: if the gate never mounts or throws,
            // nothing records.
            //
            // This flag is why PostHog's project-level "Record user sessions"
            // toggle being ON cannot by itself resume recording the signed-in
            // app, which is how 5,481 sessions were captured across `/a/*`.
            disable_session_recording: true,
            // Console logs are NOT covered by the text mask below, and were part
            // of what made "masked replay" unsupportable. Off, permanently.
            enable_recording_console_log: false,
            // When the gate does start a recording, it is masked to the maximum
            // the SDK offers: every input and every text node. That is what makes
            // recording the landing page — including the pre-auth discovery chat,
            // where prospects type about their business — a deliberate choice
            // rather than a leak. We keep layout, clicks and rage-clicks; never
            // the words. Mirror this in the project's masking config too: relying
            // on client-side settings alone is precisely what failed before.
            session_recording: {
              maskAllInputs: true,
              maskTextSelector: "*",
            },
            // Inject the CURRENT experiment variants into every event at send time
            // — including autocaptured $pageview/$pageleave and replays — so PostHog
            // segmentation matches Plausible's per-event customProperties rather
            // than a one-time register() snapshot that is empty before
            // ExperimentsProvider mounts and stale after SPA navigation. Explicit
            // event props win over these.
            before_send: (event) => {
              if (event) {
                // Scrub URLs LAST, over the merged bag: autocaptured
                // $pageview/$pageleave carry $current_url/$referrer straight
                // from the browser, and our paths name customers, projects and
                // files (lib/analytics-url.ts).
                event.properties = scrubUrlProperties({
                  ...getBaseAnalyticsProps(),
                  ...event.properties,
                });
              }
              return event;
            },
            loaded: () => {
              // environment is static for the page's lifetime, so a one-time
              // super-property is fine (unlike the experiment props above).
              posthog.register({ environment: environment ?? "development" });
              setPosthogClient(posthog);
              // Apply the *current* consent (read now, after the load window, so a
              // change made mid-load is honored). When granted, opt_in_capturing
              // emits the single deferred pageview; when not, the SDK stays dark.
              setPosthogConsent(readStoredConsent()?.analytics === true);
              // Flush events buffered during the async SDK load (only consented
              // events were buffered; see lib/posthog-events.ts).
              flushPosthogQueue();
            },
          });
        })
        .catch(() => {
          if (attempt + 1 < MAX_LOAD_ATTEMPTS) {
            setTimeout(() => {
              load(attempt + 1);
            }, 500 * 2 ** attempt);
          } else {
            // Give up after retries; allow a remount to try again.
            initialized = false;
          }
        });
    };

    load(0);
  }, [apiKey, apiHost, uiHost, environment, captureBotTraffic]);

  return null;
}
